import { formatCurrency } from "@/helpers/currency-helpers";
import { IOrderPayment } from "@/interfaces/order.interfaces";

export type GroupedOrderPaymentDisplay = {
  id: string;
  paymentMethodName: string;
  paymentMethodType: string;
  billingType: string | null;
  summaryLabel: string;
  summaryAmount: string;
  installmentCount: number | null;
  paymentComments: string | null;
  payments: IOrderPayment[];
};

const CREDIT_CARD_BILLING = "CREDIT_CARD";
const PIX_BILLING = "PIX";
const BOLETO_BILLING = "BOLETO";

function normalizeBillingType(payment: IOrderPayment): string | null {
  const billingType = (payment as IOrderPayment & { billingType?: string | null }).billingType;
  if (billingType) return billingType.toUpperCase();

  const method = `${payment.paymentMethodName} ${payment.paymentMethodType}`.toUpperCase();
  if (method.includes("PIX")) return PIX_BILLING;
  if (method.includes("BOLETO")) return BOLETO_BILLING;
  if (method.includes("CART") || method.includes("CRÉDITO") || method.includes("CREDIT"))
    return CREDIT_CARD_BILLING;

  return null;
}

function buildGroupKey(payment: IOrderPayment, billingType: string | null): string {
  if (billingType === CREDIT_CARD_BILLING) {
    const transactionId =
      payment.paymentCcGatewayTran ||
      (payment as IOrderPayment & { externalPaymentId?: string | null }).externalPaymentId;
    if (transactionId) return `${CREDIT_CARD_BILLING}|${transactionId}`;

    const installmentCount = payment.paymentCcInstallment ?? 1;
    if (installmentCount > 1) {
      return `${CREDIT_CARD_BILLING}|${payment.paymentMethodName}|${installmentCount}`;
    }
  }

  if (billingType === PIX_BILLING || billingType === BOLETO_BILLING) {
    const transactionId =
      payment.paymentCcGatewayTran ||
      (payment as IOrderPayment & { externalPaymentId?: string | null }).externalPaymentId;
    if (transactionId) return `${billingType}|${transactionId}`;
    return `${billingType}|${payment.id ?? payment.installmentNo}`;
  }

  return `${payment.paymentMethodName}|${payment.id ?? payment.installmentNo}`;
}

function resolvePaymentComments(payments: IOrderPayment[]): string | null {
  for (const payment of payments) {
    const comments = (payment as IOrderPayment & { paymentComments?: string | null }).paymentComments;
    if (comments?.trim()) return comments.trim();
  }
  return null;
}

function buildCreditCardSummary(payments: IOrderPayment[]): Pick<GroupedOrderPaymentDisplay, "summaryLabel" | "summaryAmount" | "installmentCount"> {
  const first = payments[0];
  const installmentCount = first.paymentCcInstallment ?? payments.length;
  const installmentAmount = Number(first.paymentAmount) || 0;
  const totalAmount = payments.reduce((sum, payment) => sum + (Number(payment.paymentAmount) || 0), 0);

  if (installmentCount > 1) {
    return {
      installmentCount,
      summaryLabel: `${first.paymentMethodName || "Cartão"} — ${installmentCount}x de ${formatCurrency(installmentAmount, "pt-BR", "BRL")}`,
      summaryAmount: formatCurrency(totalAmount, "pt-BR", "BRL"),
    };
  }

  return {
    installmentCount: 1,
    summaryLabel: first.paymentMethodName || "Cartão",
    summaryAmount: formatCurrency(installmentAmount, "pt-BR", "BRL"),
  };
}

function buildSinglePaymentSummary(payment: IOrderPayment): Pick<GroupedOrderPaymentDisplay, "summaryLabel" | "summaryAmount" | "installmentCount"> {
  const amount = Number(payment.paymentAmount) || 0;
  return {
    installmentCount: payment.paymentCcInstallment,
    summaryLabel: payment.paymentMethodName || payment.paymentMethodType || "Pagamento",
    summaryAmount: formatCurrency(amount, "pt-BR", "BRL"),
  };
}

export function groupOrderPaymentsForDisplay(payments: IOrderPayment[] = []): GroupedOrderPaymentDisplay[] {
  if (!payments.length) return [];

  const groups = new Map<string, IOrderPayment[]>();

  for (const payment of payments) {
    const billingType = normalizeBillingType(payment);
    const key = buildGroupKey(payment, billingType);
    const existing = groups.get(key) ?? [];
    existing.push(payment);
    groups.set(key, existing);
  }

  return Array.from(groups.entries()).map(([key, groupedPayments]) => {
    const sortedPayments = [...groupedPayments].sort(
      (a, b) => Number(a.installmentNo || 0) - Number(b.installmentNo || 0)
    );
    const first = sortedPayments[0];
    const billingType = normalizeBillingType(first);
    const isCreditCardGroup =
      billingType === CREDIT_CARD_BILLING &&
      (first.paymentCcInstallment ?? 1) > 1 &&
      sortedPayments.length > 1;

    const summary = isCreditCardGroup
      ? buildCreditCardSummary(sortedPayments)
      : buildSinglePaymentSummary(first);

    return {
      id: key,
      paymentMethodName: first.paymentMethodName,
      paymentMethodType: first.paymentMethodType,
      billingType,
      paymentComments: resolvePaymentComments(sortedPayments),
      payments: sortedPayments,
      ...summary,
    };
  });
}

export function resolvePaymentIconKey(group: GroupedOrderPaymentDisplay): string {
  const billingType = group.billingType?.toUpperCase();
  if (billingType === PIX_BILLING) return "PIX";
  if (billingType === BOLETO_BILLING) return "BOLETO";
  if (billingType === CREDIT_CARD_BILLING) return "CARTÃO";

  const method = `${group.paymentMethodName} ${group.paymentMethodType}`.toUpperCase();
  if (method.includes("PIX")) return "PIX";
  if (method.includes("BOLETO")) return "BOLETO";
  return "CARTÃO";
}
